import React, { Component } from 'react';
// TODO: Migrate module so that you don't import from the corp project in a global module
/* eslint-disable restrict-imports/restrict-imports */
import { Label, Button } from 'design-system/Form';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { SelectOption } from 'corp/components/UI/Select/helpers';
import Select from 'corp/components/UI/Select';
import { IUserJson } from '@scale/shared/types/user';

interface ICorpRole {
  _id: string;
  name: string;
  permissionLevel?: string;
  type?: string;
}

interface IBulkUserResult {
  email: string;
  userId?: string;
  found: boolean;
  roles?: ICorpRole[];
  error?: string;
}

interface IBulkRevokeResult {
  email: string;
  roleName: string;
  success: boolean;
  message: string;
}

type Mode = 'single' | 'bulk';
type BulkUserFilter = 'all' | 'withRole' | 'withoutRole';

interface IRevokeCorpPermissionRoleProps {
  showRole?: boolean;
  usersWithRole?: {
    email: string;
    _id: string;
  }[];
  corpPermissionRoleId?: string;
}

interface IRevokeCorpPermissionRoleState {
  mode: Mode;
  // Single user mode
  selectedUser?: SelectOption;
  userCorpRoles: ICorpRole[];
  selectedRoles: Set<string>;
  // Bulk mode
  bulkEmailInput: string;
  bulkUsers: IBulkUserResult[];
  bulkRoleInput: string;
  bulkResults: IBulkRevokeResult[];
  bulkUserFilter: BulkUserFilter;
  // Shared
  loading: boolean;
  loadingRoles: boolean;
  message?: string;
  succeeded?: boolean;
}

export default class RevokeCorpPermissionRole extends Component<
  IRevokeCorpPermissionRoleProps,
  IRevokeCorpPermissionRoleState
> {
  initialState: IRevokeCorpPermissionRoleState = {
    mode: 'single',
    // Single user mode
    selectedUser: undefined,
    userCorpRoles: [],
    selectedRoles: new Set(),
    // Bulk mode
    bulkEmailInput: '',
    bulkUsers: [],
    bulkRoleInput: '',
    bulkResults: [],
    bulkUserFilter: 'all',
    // Shared
    loading: false,
    loadingRoles: false,
    message: undefined,
    succeeded: undefined,
  };

  constructor(props: IRevokeCorpPermissionRoleProps, context: $FIXME) {
    super(props, context);
    this.state = this.initialState;
  }

  static defaultProps = {
    showRole: true,
    usersWithRole: [],
  };

  // Track request ID to ignore stale responses from slow queries
  private currentRequestId = 0;
  // Cache last valid results to show while new search is loading
  private lastValidOptions: SelectOption[] = [];

  // Fetch ALL users by email
  fetchUsersByEmail = async (email: string): Promise<{ options: SelectOption[] }> => {
    // Require at least 4 characters to avoid slow broad queries
    if (!email || email.length < 4) {
      return { options: this.lastValidOptions };
    }

    // Increment request ID to track latest request
    const requestId = ++this.currentRequestId;

    try {
      const { users } = await apiFetch<{ users: IUserJson[] }>(
        null,
        `corp-api/find/usersByEmail?email=${encodeURIComponent(email)}`,
      );

      // Only use result if this is still the latest request
      if (requestId === this.currentRequestId) {
        const searchTerm = email.toLowerCase();
        // Filter to only include emails where search term appears as consecutive substring
        const options = users
          .filter(user => user.email.toLowerCase().includes(searchTerm))
          .map(user => ({
            label: user.email,
            value: user.id,
          }));
        // Cache the valid results
        this.lastValidOptions = options;
        return { options };
      }
      // Stale request - return cached options to keep dropdown populated
      return { options: this.lastValidOptions };
    } catch (error: any) {
      // Only show error if this is still the latest request
      if (requestId === this.currentRequestId) {
        defaultApiFetchErrorHandler(error, { handleHttp: true });
      }
      // Return cached options on error
      return { options: this.lastValidOptions };
    }
  };

  onSelectUser = async (selectedUser?: SelectOption): Promise<void> => {
    if (!selectedUser) return;

    this.setState({
      selectedUser,
      userCorpRoles: [],
      selectedRoles: new Set(),
      loadingRoles: true,
      message: undefined,
      succeeded: undefined,
    });

    try {
      // Fetch user details including their corp roles
      const userData = await apiFetch(null, `corp-api/lookup/${selectedUser.value}`);

      // Extract corp roles from user data
      const corpRoles: ICorpRole[] = userData?.userIdentity?.corpRoles || userData?.corpRoles || [];

      this.setState({
        userCorpRoles: corpRoles,
        loadingRoles: false,
      });
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      this.setState({
        userCorpRoles: [],
        loadingRoles: false,
        message: 'Failed to load user permissions',
        succeeded: false,
      });
    }
  };

  onToggleRole = (roleId: string): void => {
    const selectedRoles = new Set(this.state.selectedRoles);
    if (selectedRoles.has(roleId)) {
      selectedRoles.delete(roleId);
    } else {
      selectedRoles.add(roleId);
    }
    this.setState({ selectedRoles, message: undefined, succeeded: undefined });
  };

  onSelectAll = (): void => {
    const allRoleIds = this.state.userCorpRoles.map(role => role._id);
    this.setState({
      selectedRoles: new Set(allRoleIds),
      message: undefined,
      succeeded: undefined,
    });
  };

  onDeselectAll = (): void => {
    this.setState({
      selectedRoles: new Set(),
      message: undefined,
      succeeded: undefined,
    });
  };

  onReset = (): void => {
    this.lastValidOptions = []; // Clear cached search results
    this.currentRequestId = 0; // Reset request tracking
    this.setState({ ...this.initialState, mode: this.state.mode });
  };

  // ============ BULK MODE HANDLERS ============

  onModeChange = (mode: Mode): void => {
    this.lastValidOptions = [];
    this.currentRequestId = 0;
    this.setState({ ...this.initialState, mode });
  };

  onBulkEmailInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    this.setState({ bulkEmailInput: e.target.value, message: undefined, succeeded: undefined });
  };

  onBulkRoleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({ bulkRoleInput: e.target.value, message: undefined, succeeded: undefined });
  };

  parseEmails = (input: string): string[] => {
    // Split by comma, newline, semicolon, or whitespace
    const emails = input
      .split(/[,;\s\n]+/)
      .map(email => email.trim().toLowerCase())
      .filter(email => email.length > 0 && email.includes('@'));
    // Remove duplicates
    return [...new Set(emails)];
  };

  // Check if a user has a role matching the input
  userHasMatchingRole = (user: IBulkUserResult, roleName: string): boolean => {
    if (!roleName.trim() || !user.roles || user.roles.length === 0) return false;
    const searchTerm = roleName.trim().toLowerCase();
    return user.roles.some(
      r =>
        r._id === roleName.trim() ||
        r.name.toLowerCase() === searchTerm ||
        r.name.toLowerCase().includes(searchTerm),
    );
  };

  // Get the matching role for a user
  getMatchingRole = (user: IBulkUserResult, roleName: string): ICorpRole | undefined => {
    if (!roleName.trim() || !user.roles) return undefined;
    const searchTerm = roleName.trim().toLowerCase();
    return user.roles.find(
      r =>
        r._id === roleName.trim() ||
        r.name.toLowerCase() === searchTerm ||
        r.name.toLowerCase().includes(searchTerm),
    );
  };

  // Get filtered bulk users based on role input and filter selection
  getFilteredBulkUsers = (): IBulkUserResult[] => {
    const { bulkUsers, bulkRoleInput, bulkUserFilter } = this.state;
    const roleName = bulkRoleInput.trim();

    if (!roleName || bulkUserFilter === 'all') {
      return bulkUsers;
    }

    return bulkUsers.filter(user => {
      // Users not found are excluded from role-specific filters
      if (!user.found) return false;
      const hasRole = this.userHasMatchingRole(user, roleName);
      return bulkUserFilter === 'withRole' ? hasRole : !hasRole;
    });
  };

  // Get counts for filter tabs
  getRoleCounts = (): {
    total: number;
    withRole: number;
    withoutRole: number;
    notFound: number;
  } => {
    const { bulkUsers, bulkRoleInput } = this.state;
    const roleName = bulkRoleInput.trim();
    const foundUsers = bulkUsers.filter(u => u.found);
    const notFoundCount = bulkUsers.filter(u => !u.found).length;

    if (!roleName) {
      return {
        total: bulkUsers.length,
        withRole: 0,
        withoutRole: foundUsers.length,
        notFound: notFoundCount,
      };
    }

    const withRoleCount = foundUsers.filter(u => this.userHasMatchingRole(u, roleName)).length;
    return {
      total: bulkUsers.length,
      withRole: withRoleCount,
      withoutRole: foundUsers.length - withRoleCount,
      notFound: notFoundCount,
    };
  };

  onBulkUserFilterChange = (filter: BulkUserFilter): void => {
    this.setState({ bulkUserFilter: filter });
  };

  lookupBulkUsers = async (): Promise<void> => {
    const emails = this.parseEmails(this.state.bulkEmailInput);

    if (emails.length === 0) {
      this.setState({
        message: 'Please enter at least one valid email address',
        succeeded: false,
      });
      return;
    }

    this.setState({ loading: true, message: undefined, succeeded: undefined, bulkUsers: [] });

    try {
      // Look up all users in parallel
      const results = await Promise.all(
        emails.map(async (email): Promise<IBulkUserResult> => {
          try {
            // Search for user by exact email
            const { users } = await apiFetch<{ users: IUserJson[] }>(
              null,
              `corp-api/find/usersByEmail?email=${encodeURIComponent(email)}`,
            );

            // Find exact match
            const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());

            if (!user) {
              return { email, found: false, error: 'User not found' };
            }

            // Fetch user's roles
            try {
              const userData = await apiFetch(null, `corp-api/lookup/${user.id}`);
              const roles: ICorpRole[] =
                userData?.userIdentity?.corpRoles || userData?.corpRoles || [];
              return { email, userId: user.id, found: true, roles };
            } catch {
              return {
                email,
                userId: user.id,
                found: true,
                roles: [],
                error: 'Failed to load roles',
              };
            }
          } catch (error: any) {
            return { email, found: false, error: error.message || 'Lookup failed' };
          }
        }),
      );

      const foundCount = results.filter(r => r.found).length;
      this.setState({
        bulkUsers: results,
        loading: false,
        message: `Found ${foundCount} of ${results.length} users`,
        succeeded: foundCount > 0,
      });
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      this.setState({
        loading: false,
        message: 'Failed to look up users',
        succeeded: false,
      });
    }
  };

  revokeBulkRoles = async (event: React.MouseEvent<HTMLElement>): Promise<void> => {
    event.preventDefault();

    const { bulkUsers, bulkRoleInput } = this.state;
    const roleName = bulkRoleInput.trim();

    if (!roleName) {
      this.setState({ message: 'Please enter a role name or ID to revoke', succeeded: false });
      return;
    }

    const usersWithRole = bulkUsers.filter(u => u.found && u.userId);
    if (usersWithRole.length === 0) {
      this.setState({ message: 'No valid users to revoke roles from', succeeded: false });
      return;
    }

    this.setState({ loading: true, message: undefined, succeeded: undefined, bulkResults: [] });

    try {
      const results: IBulkRevokeResult[] = [];
      const roleToUserIds: Record<string, { ids: string[]; name: string }> = {};

      // Group users by the role ID we need to revoke
      usersWithRole.forEach(user => {
        const matchingRole = user.roles?.find(
          r =>
            r._id === roleName ||
            r.name.toLowerCase() === roleName.toLowerCase() ||
            r.name.toLowerCase().includes(roleName.toLowerCase()),
        );

        if (matchingRole) {
          if (!roleToUserIds[matchingRole._id]) {
            roleToUserIds[matchingRole._id] = { ids: [], name: matchingRole.name };
          }
          roleToUserIds[matchingRole._id].ids.push(user.userId!);
        } else {
          results.push({
            email: user.email,
            roleName,
            success: false,
            message: 'Role not found for this user',
          });
        }
      });

      // Revoke in batches per role ID
      await Promise.all(
        Object.entries(roleToUserIds).map(async ([roleId, { ids, name }]) => {
          try {
            const { success, retMessage } = await apiFetch(
              null,
              'corp-api/revoke_corp_permission_role',
              {
                method: 'POST',
                body: { corpPermissionRoleId: roleId, userIds: ids },
              },
            );

            ids.forEach(userId => {
              const userEmail = usersWithRole.find(u => u.userId === userId)?.email || userId;
              results.push({
                email: userEmail,
                roleName: name,
                success,
                message: retMessage || (success ? 'Revoked' : 'Failed'),
              });
            });
          } catch (error: any) {
            ids.forEach(userId => {
              const userEmail = usersWithRole.find(u => u.userId === userId)?.email || userId;
              results.push({
                email: userEmail,
                roleName: name,
                success: false,
                message: error.message || 'Failed to revoke',
              });
            });
          }
        }),
      );

      const successCount = results.filter(r => r.success).length;
      const allSucceeded = successCount === results.length;

      this.setState({
        bulkResults: results,
        loading: false,
        succeeded: allSucceeded,
        message: allSucceeded
          ? `Successfully revoked role from ${successCount} user(s)`
          : `Revoked from ${successCount}/${results.length} users`,
      });
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      this.setState({
        loading: false,
        message: 'Failed to revoke roles',
        succeeded: false,
      });
    }
  };

  // ============ SINGLE MODE HANDLERS ============

  revokeSelectedRoles = async (event: React.MouseEvent<HTMLElement>): Promise<void> => {
    event.preventDefault();

    const { selectedUser, selectedRoles, userCorpRoles } = this.state;
    if (!selectedUser || selectedRoles.size === 0) return;

    this.setState({ loading: true, message: undefined, succeeded: undefined });

    try {
      const userId = selectedUser.value;

      // Revoke all selected roles in parallel for speed
      const results = await Promise.all(
        Array.from(selectedRoles).map(async roleId => {
          const role = userCorpRoles.find(r => r._id === roleId);
          try {
            const { success, retMessage } = await apiFetch(
              null,
              `corp-api/revoke_corp_permission_role`,
              {
                method: 'POST',
                body: { corpPermissionRoleId: roleId, userIds: [userId] },
              },
            );
            return { success, roleName: role?.name || roleId, message: retMessage };
          } catch (error) {
            return {
              success: false,
              roleName: role?.name || roleId,
              message: error.message || 'Failed',
            };
          }
        }),
      );

      const successCount = results.filter(r => r.success).length;
      const allSucceeded = successCount === results.length;

      // Refresh user's roles after revoking
      if (successCount > 0) {
        await this.onSelectUser(selectedUser);
      }

      this.setState({
        succeeded: allSucceeded,
        message: allSucceeded
          ? `Successfully revoked ${successCount} role(s) from ${selectedUser.label}`
          : `Revoked ${successCount}/${results.length} roles. Failed: ${results
              .filter(r => !r.success)
              .map(r => r.roleName)
              .join(', ')}`,
        selectedRoles: new Set(),
      });
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      this.setState({ succeeded: false, message: 'Failed to revoke roles' });
    } finally {
      this.setState({ loading: false });
    }
  };

  renderBulkResults() {
    const { bulkResults } = this.state;

    return (
      <div className="section">
        <Label>Results</Label>
        <div className="bulkResultsTable">
          <div className="resultsHeader">
            <span>Email</span>
            <span>Role</span>
            <span>Status</span>
          </div>
          {bulkResults.map((result, idx) => {
            const rowClass = result.success ? 'success' : 'failure';
            return (
              <div key={idx} className={`resultRow ${rowClass}`}>
                <span className="resultEmail">{result.email}</span>
                <span className="resultRole">{result.roleName}</span>
                <span className={`resultStatus ${rowClass}`}>
                  {result.success ? '✓ ' : '✗ '}
                  {result.message}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderBulkUserItem(
    user: IBulkUserResult,
    idx: number,
    hasRoleInput: boolean,
    bulkRoleInput: string,
  ) {
    const matchingRole = hasRoleInput ? this.getMatchingRole(user, bulkRoleInput) : undefined;
    const itemClass = user.found ? (matchingRole ? 'hasMatchingRole' : 'found') : 'notFound';

    return (
      <div key={idx} className={`bulkUserItem ${itemClass}`}>
        <div className="userMainInfo">
          <span className="bulkUserEmail">{user.email}</span>
          {user.found && matchingRole && (
            <span className="matchedRoleBadge">{matchingRole.name}</span>
          )}
        </div>
        <div className="userStatusInfo">
          {!user.found ? (
            <span className="statusBadge error">{user.error || 'Not Found'}</span>
          ) : matchingRole ? (
            <span className="statusBadge willRevoke">Will Revoke</span>
          ) : hasRoleInput ? (
            <span className="statusBadge noMatch">No Match</span>
          ) : (
            <span className="statusBadge found">{user.roles?.length || 0} role(s)</span>
          )}
        </div>
      </div>
    );
  }

  renderRoleInput() {
    const { bulkRoleInput, loading } = this.state;
    return (
      <div className="section">
        <Label>Role to Revoke</Label>
        <input
          type="text"
          className="bulkRoleInput"
          placeholder="Enter the role name or ID you want to revoke"
          value={bulkRoleInput}
          onChange={this.onBulkRoleInputChange}
          disabled={loading}
        />
      </div>
    );
  }

  renderEmailInput() {
    const { bulkEmailInput, loading } = this.state;
    return (
      <div className="section">
        <Label>Email Addresses</Label>
        <textarea
          className="bulkEmailInput"
          placeholder="Enter email addresses (one per line, or separated by commas)"
          value={bulkEmailInput}
          onChange={this.onBulkEmailInputChange}
          disabled={loading}
        />
        <div className="inputActions">
          <div className="emailCount">
            {this.parseEmails(bulkEmailInput).length} email(s) entered
          </div>
          <div>
            <Button
              type="button"
              disabled={loading || this.parseEmails(bulkEmailInput).length === 0}
              onClick={this.lookupBulkUsers}
            >
              {loading ? 'Looking up...' : 'Look Up Users'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  renderLookupSummary(foundCount: number, counts: { notFound: number }) {
    return (
      <div className="lookupSummary">
        <div className="summaryText">
          <strong>{foundCount}</strong> of <strong>{foundCount + counts.notFound}</strong> users
          found
          {counts.notFound > 0 && (
            <span className="notFoundNote"> ({counts.notFound} not found)</span>
          )}
        </div>
        <button type="button" className="linkButton" onClick={this.onReset}>
          Clear & Start Over
        </button>
      </div>
    );
  }

  renderNoMatchUsers(bulkRoleInput: string) {
    const { bulkUsers } = this.state;
    const noMatchUsers = bulkUsers.filter(
      user => !user.found || !this.userHasMatchingRole(user, bulkRoleInput),
    );

    return (
      <div className="section noMatchSection">
        <b>Errors:</b>
        <table className="noMatchTable">
          <colgroup>
            <col className="colEmail" />
            <col className="colSpacer" />
            <col className="colStatus" />
          </colgroup>
          <tbody>
            {noMatchUsers.map((user, idx) => (
              <tr key={idx} className={user.found ? 'noMatch' : 'notFound'}>
                <td className="noMatchEmail">{user.email}</td>
                <td className="colSpacer" />
                <td className={`noMatchStatus ${user.found ? 'noMatch' : 'error'}`}>
                  {user.found ? 'No matching role' : 'User not found'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  renderRolesList() {
    const { userCorpRoles, selectedRoles, loadingRoles } = this.state;

    if (loadingRoles) {
      return <div className="loading">Loading user permissions...</div>;
    }

    if (userCorpRoles.length === 0) {
      return <div className="noRoles">No corp permission roles found for this user.</div>;
    }

    return (
      <div className="rolesList">
        <div className="rolesHeader">
          <span>
            Found <b>{userCorpRoles.length}</b> permission role(s)
          </span>
          <div className="selectActions">
            <button type="button" className="linkButton" onClick={this.onSelectAll}>
              Select All
            </button>
            <span className="separator">|</span>
            <button type="button" className="linkButton" onClick={this.onDeselectAll}>
              Deselect All
            </button>
          </div>
        </div>
        <div className="rolesContainer">
          {userCorpRoles.map(role => (
            <label key={role._id} className="roleItem">
              <input
                type="checkbox"
                checked={selectedRoles.has(role._id)}
                onChange={() => this.onToggleRole(role._id)}
                disabled={this.state.loading}
              />
              <div className="roleInfo">
                <span className="roleName">{role.name}</span>
                {role.permissionLevel && (
                  <span className="roleLevel">({role.permissionLevel})</span>
                )}
                {role.type && <span className="roleType">[{role.type}]</span>}
              </div>
              <a
                href={`lookup/${role._id}`}
                className="lookupLink"
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
              >
                View
              </a>
            </label>
          ))}
        </div>
      </div>
    );
  }

  renderBulkMode() {
    const { bulkRoleInput, loading, bulkUsers, bulkResults, message, succeeded } = this.state;
    const counts = this.getRoleCounts();
    const hasRoleInput = bulkRoleInput.trim().length > 0;
    const foundCount = bulkUsers.filter(u => u.found).length;

    return (
      <div className="bulkContainer">
        {/* Email Input Section */}
        {this.renderEmailInput()}

        {/* Users Found Summary */}
        {bulkUsers.length > 0 && this.renderLookupSummary(foundCount, counts)}

        {/* Role Input - Only show after users are looked up */}
        {bulkUsers.length > 0 && foundCount > 0 && this.renderRoleInput()}

        {/* Matched Users Summary */}
        {bulkUsers.length > 0 && hasRoleInput && counts.withRole > 0 && (
          <div className="matchedSummary">
            <span className="matchedIcon">✓</span>
            <span className="matchedText">
              <strong>{counts.withRole}</strong> user(s) have this role and will be revoked
            </span>
          </div>
        )}

        {/* Only show users with NO match */}
        {bulkUsers.length > 0 &&
          hasRoleInput &&
          (counts.withoutRole > 0 || counts.notFound > 0) &&
          this.renderNoMatchUsers(bulkRoleInput)}

        {/* Action Buttons */}
        {bulkUsers.length > 0 && hasRoleInput && counts.withRole > 0 && (
          <div className="buttons">
            <Button primary type="button" disabled={loading} onClick={this.revokeBulkRoles}>
              {loading ? 'Revoking...' : `Revoke Role from ${counts.withRole} User(s)`}
            </Button>
          </div>
        )}

        {/* Results */}
        {bulkResults.length > 0 && this.renderBulkResults()}

        {message && <div className={succeeded ? 'successMessage' : 'errorMessage'}>{message}</div>}
      </div>
    );
  }

  renderRevokeCorpPermissionRole() {
    const { mode, selectedUser, selectedRoles, loading } = this.state;

    return (
      <div className="container">
        <div className="text">
          <b>Revoke Corp Permission Roles</b>
          <br />
          Manage corp permission role revocations for one or many users.
        </div>

        <div className="modeTabs">
          <button
            type="button"
            className={`modeTab ${mode === 'single' ? 'active' : ''}`}
            onClick={() => this.onModeChange('single')}
          >
            Single User
          </button>
          <button
            type="button"
            className={`modeTab ${mode === 'bulk' ? 'active' : ''}`}
            onClick={() => this.onModeChange('bulk')}
          >
            Bulk Emails
          </button>
        </div>

        {mode === 'single' ? (
          <>
            {!selectedUser ? (
              <div className="section">
                <Label>Search User by Email</Label>
                <Select
                  placeholder="Type at least 4 characters to search by email..."
                  disabled={loading}
                  onChange={this.onSelectUser}
                  filterOption={() => true}
                  loadOptions={this.fetchUsersByEmail}
                  autoload={false}
                  searchPromptText="Type at least 4 characters..."
                  loadingPlaceholder=""
                  cache={false}
                  onBlurResetsInput={false}
                  onCloseResetsInput={false}
                  onSelectResetsInput={false}
                />
              </div>
            ) : (
              <>
                <div className="section">
                  <Label>Selected User</Label>
                  <div className="selectedUserCard">
                    <div className="userInfo">
                      <span className="userEmail">{selectedUser.label}</span>
                      <span className="userId">ID: {selectedUser.value}</span>
                    </div>
                    <button
                      type="button"
                      className="changeUserButton"
                      onClick={this.onReset}
                      disabled={loading}
                    >
                      Change User
                    </button>
                  </div>
                </div>

                <div className="section">
                  <Label>User's Corp Permission Roles</Label>
                  {this.renderRolesList()}
                </div>

                <div className="buttons">
                  <Button
                    primary
                    type="button"
                    disabled={loading || !selectedUser || selectedRoles.size === 0}
                    onClick={this.revokeSelectedRoles}
                  >
                    {loading
                      ? 'Revoking...'
                      : `Revoke ${
                          selectedRoles.size > 0 ? `${selectedRoles.size} ` : ''
                        }Selected Role(s)`}
                  </Button>
                  &emsp;&emsp;
                  <Button type="button" disabled={loading} onClick={this.onReset}>
                    Reset
                  </Button>
                </div>

                {this.state.message && (
                  <div className={this.state.succeeded ? 'successMessage' : 'errorMessage'}>
                    {this.state.message}
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          this.renderBulkMode()
        )}

        <style jsx>{`
          .container {
            max-width: 800px;
            min-height: 250px;
            padding-bottom: 20px;
            margin: 20px 20px;
            font-weight: normal;
          }
          .text {
            margin-bottom: 16px;
            line-height: 1.5;
          }
          .modeTabs {
            display: flex;
            gap: 2px;
            margin-bottom: 24px;
            border-bottom: 1px solid #ddd;
          }
          .modeTab {
            padding: 10px 20px;
            background: #f8f8f8;
            border: 1px solid #ddd;
            border-bottom: none;
            border-top-left-radius: 6px;
            border-top-right-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            color: #666;
            transition: all 0.2s;
          }
          .modeTab:hover:not(.active) {
            background: #eee;
          }
          .modeTab.active {
            background: #fff;
            color: #0066cc;
            font-weight: 600;
            margin-bottom: -1px;
            border-bottom: 1px solid #fff;
            box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
          }
          .section {
            margin-bottom: 28px;
          }
          .inputActions {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
            margin-top: 14px;
            margin-bottom: 20px;
            padding-top: 14px;
            border-top: 1px solid #eee;
          }
          .emailCount {
            display: block;
            width: 100%;
            font-size: 13px;
            color: #666;
            margin-bottom: 8px;
          }
          .bulkEmailInput {
            width: 100%;
            min-height: 100px;
            padding: 12px;
            border: 1px solid #ccc;
            border-radius: 6px;
            font-family: inherit;
            font-size: 14px;
            line-height: 1.5;
            resize: vertical;
          }
          .bulkEmailInput:focus {
            outline: none;
            border-color: #0066cc;
          }
          .bulkEmailInput::placeholder {
            color: #999;
          }
          .lookupSummary {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px 20px;
            background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
            border: 1px solid #d0d9ff;
            border-radius: 8px;
            margin-bottom: 28px;
          }
          .summaryText {
            font-size: 14px;
            color: #333;
          }
          .notFoundNote {
            color: #b00;
            font-size: 13px;
            margin-left: 6px;
          }
          .bulkRoleInput {
            width: 100%;
            padding: 12px 14px;
            border: 1px solid #ccc;
            border-radius: 6px;
            font-size: 14px;
          }
          .bulkRoleInput:focus {
            outline: none;
            border-color: #0066cc;
          }
          .matchedSummary {
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 18px 22px;
            background: linear-gradient(135deg, #f0fff4 0%, #dcfce7 100%);
            border: 1px solid #86efac;
            border-radius: 8px;
            margin-bottom: 28px;
          }
          .matchedIcon {
            width: 32px;
            height: 32px;
            background: #16a34a;
            color: #fff;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            font-weight: bold;
            flex-shrink: 0;
          }
          .matchedText {
            font-size: 15px;
            color: #166534;
            font-weight: 500;
          }
          .noMatchSection {
            margin-bottom: 36px;
          }
          .noMatchSection :global(label) {
            display: block;
            margin-bottom: 14px;
            font-size: 14px;
            font-weight: 600;
            color: #374151;
          }
          .noMatchTable {
            width: 100%;
            border-collapse: collapse;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            font-size: 13px;
            table-layout: fixed;
          }
          .noMatchTable td {
            padding: 12px 16px;
            text-align: left;
            vertical-align: top;
            border-bottom: 1px solid #f3f4f6;
          }
          .noMatchTable tr:last-child td {
            border-bottom: none;
          }
          .noMatchTable tr.notFound {
            background: #fef2f2;
          }
          .noMatchTable tr.noMatch {
            background: #f9fafb;
          }
          .noMatchTable col.colEmail {
            width: 50%;
          }
          .noMatchTable col.colSpacer {
            width: 30%;
          }
          .noMatchTable col.colStatus {
            width: 35%;
          }
          .noMatchTable td.colSpacer {
            padding: 0;
          }
          .noMatchEmail {
            font-family: ui-monospace, monospace;
            color: #1f2937;
            word-wrap: break-word;
            word-break: break-all;
          }
          .noMatchStatus {
            font-weight: 500;
            white-space: nowrap;
          }
          .noMatchStatus.error {
            color: #dc2626;
          }
          .noMatchStatus.noMatch {
            color: #6b7280;
          }
          .bulkUserList {
            max-height: 250px;
            overflow-y: auto;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            background: #fff;
          }
          .emptyState {
            padding: 30px;
            text-align: center;
            color: #666;
            font-size: 14px;
          }
          .bulkUserItem {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 14px 18px;
            border-bottom: 1px solid #f3f4f6;
            font-size: 13px;
          }
          .bulkUserItem:last-child {
            border-bottom: none;
          }
          .bulkUserItem:hover {
            background: #f9fafb;
          }
          .bulkUserItem.found {
            background: #fff;
          }
          .bulkUserItem.hasMatchingRole {
            background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
            border-left: 3px solid #0066cc;
          }
          .bulkUserItem.notFound {
            background: #fef2f2;
            border-left: 4px solid #ef4444;
          }
          .bulkUserItem.noMatch {
            background: #f9fafb;
            border-left: 4px solid #9ca3af;
          }
          .userMainInfo {
            display: flex;
            align-items: center;
            gap: 12px;
            flex: 1;
            min-width: 0;
          }
          .bulkUserEmail {
            font-size: 14px;
            color: #1f2937;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-family: ui-monospace, monospace;
          }
          .matchedRoleBadge {
            background: #0066cc;
            color: #fff;
            padding: 3px 10px;
            border-radius: 3px;
            font-size: 11px;
            font-weight: 500;
            white-space: nowrap;
          }
          .userStatusInfo {
            flex-shrink: 0;
            margin-left: 20px;
          }
          .statusBadge {
            padding: 6px 12px;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 500;
          }
          .statusBadge.error {
            background: #fef2f2;
            color: #dc2626;
            border: 1px solid #fecaca;
          }
          .statusBadge.willRevoke {
            background: rgba(0, 102, 204, 0.1);
            color: #0066cc;
          }
          .statusBadge.noMatch {
            background: #f3f4f6;
            color: #6b7280;
            border: 1px solid #e5e7eb;
          }
          .statusBadge.found {
            background: rgba(0, 102, 204, 0.08);
            color: #0066cc;
          }
          .bulkResultsTable {
            border: 1px solid #ddd;
            border-radius: 4px;
            overflow: hidden;
          }
          .resultsHeader {
            display: grid;
            grid-template-columns: 2fr 1fr 1.5fr;
            background: #f5f5f5;
            padding: 12px 16px;
            font-weight: 600;
            font-size: 13px;
            border-bottom: 1px solid #ddd;
          }
          .resultRow {
            display: grid;
            grid-template-columns: 2fr 1fr 1.5fr;
            padding: 12px 16px;
            border-bottom: 1px solid #eee;
            font-size: 13px;
            align-items: center;
          }
          .resultRow:last-child {
            border-bottom: none;
          }
          .resultEmail {
            color: #333;
          }
          .resultRole {
            color: #666;
          }
          .resultStatus.success {
            color: #0a7d00;
          }
          .resultStatus.failure {
            color: #b00;
          }
          .selectedUserCard {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px;
            background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
            border: 1px solid #d0d9ff;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 102, 204, 0.08);
          }
          .userInfo {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          .userEmail {
            font-weight: 600;
            font-size: 15px;
            color: #1a1a2e;
          }
          .userId {
            font-size: 12px;
            color: #666;
            font-family: monospace;
          }
          .changeUserButton {
            padding: 8px 16px;
            background: #fff;
            border: 1px solid #ccc;
            border-radius: 6px;
            color: #333;
            font-size: 13px;
            cursor: pointer;
            transition: all 0.15s ease;
          }
          .changeUserButton:hover:not(:disabled) {
            background: #f5f5f5;
            border-color: #999;
          }
          .changeUserButton:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .loading {
            padding: 20px;
            text-align: center;
            color: #666;
          }
          .noRoles {
            padding: 20px;
            text-align: center;
            color: #666;
            background: #f5f5f5;
            border-radius: 4px;
          }
          .rolesList {
            border: 1px solid #ddd;
            border-radius: 4px;
            overflow: hidden;
          }
          .rolesHeader {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 16px;
            background: #f5f5f5;
            border-bottom: 1px solid #ddd;
          }
          .selectActions {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .linkButton {
            background: #fff;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            color: #374151;
            cursor: pointer;
            font-size: 13px;
            padding: 8px 14px;
            transition: all 0.15s;
          }
          .linkButton:hover {
            background: #f9fafb;
            border-color: #9ca3af;
          }
          .separator {
            color: #ccc;
          }
          .rolesContainer {
            max-height: 400px;
            overflow-y: auto;
          }
          .roleItem {
            display: flex;
            align-items: center;
            padding: 12px 16px;
            border-bottom: 1px solid #eee;
            cursor: pointer;
            transition: background 0.15s;
          }
          .roleItem:hover {
            background: #f9f9f9;
          }
          .roleItem:last-child {
            border-bottom: none;
          }
          .roleItem input[type='checkbox'] {
            margin-right: 12px;
            width: 18px;
            height: 18px;
            cursor: pointer;
          }
          .roleInfo {
            flex: 1;
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
          }
          .roleName {
            font-weight: 500;
            color: #333;
          }
          .roleLevel {
            color: #666;
            font-size: 13px;
          }
          .roleType {
            color: #999;
            font-size: 12px;
            background: #eee;
            padding: 2px 6px;
            border-radius: 3px;
          }
          .lookupLink {
            color: #0066cc;
            font-size: 13px;
            text-decoration: none;
            padding: 4px 8px;
            border-radius: 3px;
          }
          .lookupLink:hover {
            background: #e6f0ff;
            text-decoration: underline;
          }
          .buttons {
            margin-top: 8px;
            padding-top: 24px;
            text-align: center;
            border-top: 1px solid #e5e7eb;
          }
          .successMessage {
            margin-top: 16px;
            border: 1px solid rgba(0, 200, 0, 0.3);
            border-radius: 4px;
            background-color: rgba(0, 200, 0, 0.05);
            padding: 16px;
            color: #0a7d00;
          }
          .errorMessage {
            margin-top: 16px;
            border: 1px solid rgba(255, 0, 0, 0.3);
            border-radius: 4px;
            background-color: rgba(255, 0, 0, 0.03);
            padding: 16px;
            color: #b00;
          }
        `}</style>
      </div>
    );
  }

  render() {
    return <div id="body">{this.renderRevokeCorpPermissionRole()}</div>;
  }
}
